import type {
  VestiaireItemBadge,
  VestiaireSearchCriteriaInput,
  VestiaireSellerBadge,
} from '@/shared/api/types/generated';

/**
 * URL de recherche Vestiaire Collective équivalente au formulaire (spec
 * docs/superpowers/specs/2026-10-01-vestiaire-import-url-design.md § 2) : fragment par ids seuls
 * (VC n'utilise que l'id, il réécrit lui-même les libellés). Ce qui n'a pas d'équivalent sur VC est
 * rendu dans `omitted` pour être signalé à l'utilisateur.
 */

export type VestiaireUrlLanguage = 'fr' | 'en';
export type VestiaireUrlOmission =
  | 'sizes'
  | 'excludedBrands'
  | 'excludedKeywords'
  | 'keywordsOr'
  | 'vintage'
  | 'exceptional'
  | 'materials'
  | 'countries'
  | 'mixedCategoryLevels';

export interface VestiaireSearchUrl {
  url: string;
  omitted: VestiaireUrlOmission[];
}

/** Page de recherche VC nue dans la langue du compte (`fr.` en français, `www.` en anglais). */
export const VESTIAIRE_SEARCH_BASE_URL: Record<VestiaireUrlLanguage, string> = {
  fr: 'https://fr.vestiairecollective.com/search/',
  en: 'https://www.vestiairecollective.com/search/',
};

const SELLER_BADGE_IDS: Record<Exclude<VestiaireSellerBadge, '%future added value'>, string> = {
  expert: '3',
  trusted: '2',
};

const ITEM_BADGE_PARTS: Partial<Record<VestiaireItemBadge, [string, string]>> = {
  weLove: ['editorPicks', 'We love'],
  dealEligible: ['dealsEligible', '1'],
  officialStore: ['seller.isOfficialStore', '1'],
};

/** Niveau VC d'une matière : 0 ⇒ `materialParent` (materialLvl0), 1 ⇒ `material` (materialLvl1). */
const MATERIAL_KEY_BY_LEVEL: Partial<Record<number, string>> = { 0: 'materialParent', 1: 'material' };

/**
 * @param materialLevels niveau (0 ou 1) de chaque matière du dictionnaire (`materialLevelsById`) :
 *   nos ids mêlent les deux niveaux VC, qui ont chacun leur clé dans le fragment. Une matière au
 *   niveau inconnu n'est pas écrite (omission `materials`) plutôt que devinée.
 */
export function buildVestiaireSearchUrl(
  criteria: VestiaireSearchCriteriaInput,
  language: VestiaireUrlLanguage,
  countryIsoCodes: ReadonlyMap<number, string>,
  materialLevels: ReadonlyMap<number, number>,
): VestiaireSearchUrl {
  const parts: [string, string][] = [];
  const omitted: VestiaireUrlOmission[] = [];
  const ids = (key: string, values: readonly number[] | null | undefined) => {
    if (values?.length) parts.push([key, values.join('-')]);
  };
  // Catégories : nos niveaux se combinent en OU (pathsToCatalogCriteria), les clés du fragment VC
  // en ET. Plusieurs niveaux à la fois n'ont donc pas d'équivalent : aucune clé de catégorie.
  const categoryLevels: [string, readonly number[] | null | undefined][] = [
    ['gender', criteria.universeIds],
    ['categoryParent', criteria.catalogRootIds],
    ['category', criteria.catalogLevel1Ids],
    ['subcategory', criteria.catalogLevel2Ids],
  ];
  const usedCategoryLevels = categoryLevels.filter(([, values]) => values?.length);
  if (usedCategoryLevels.length > 1) omitted.push('mixedCategoryLevels');
  else for (const [key, values] of usedCategoryLevels) ids(key, values);
  ids('brand', criteria.brandIds);
  ids('model', criteria.modelIds);
  ids('condition', criteria.conditionIds);
  ids('color', criteria.colorIds);
  const materialsByKey = new Map<string, number[]>();
  let unknownMaterial = false;
  for (const id of criteria.materialIds ?? []) {
    const level = materialLevels.get(id);
    const key = level === undefined ? undefined : MATERIAL_KEY_BY_LEVEL[level];
    if (key === undefined) {
      unknownMaterial = true;
      continue;
    }
    materialsByKey.set(key, [...(materialsByKey.get(key) ?? []), id]);
  }
  ids('materialParent', materialsByKey.get('materialParent'));
  ids('material', materialsByKey.get('material'));
  const countries = (criteria.countryIds ?? [])
    .map((id) => countryIsoCodes.get(id))
    .filter((code): code is string => code != null);
  if (countries.length) parts.push(['country', countries.join('-')]);
  const sellerBadges = (criteria.sellerBadges ?? [])
    .map((badge) => (badge === '%future added value' ? undefined : SELLER_BADGE_IDS[badge]))
    .filter((id): id is string => id != null);
  if (sellerBadges.length) parts.push(['sellerBadge', sellerBadges.join('-')]);
  for (const badge of criteria.itemBadges ?? []) {
    const part = ITEM_BADGE_PARTS[badge];
    if (part) parts.push(part);
  }
  if (criteria.priceMin != null) parts.push(['priceMin', String(Math.trunc(criteria.priceMin))]);
  if (criteria.priceMax != null) parts.push(['priceMax', String(Math.trunc(criteria.priceMax))]);

  const query = criteria.keywords?.length ? `?${new URLSearchParams({ q: criteria.keywords.join(' ') })}` : '';
  const fragment = parts.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('_');

  if (criteria.sizeIds?.length) omitted.push('sizes');
  if (criteria.excludedBrandIds?.length) omitted.push('excludedBrands');
  if (criteria.excludedKeywords?.length) omitted.push('excludedKeywords');
  if (criteria.keywordsOperator === 'or') omitted.push('keywordsOr');
  if (criteria.itemBadges?.includes('vintage')) omitted.push('vintage');
  if (criteria.itemBadges?.includes('exceptional')) omitted.push('exceptional');
  if (unknownMaterial) omitted.push('materials');
  if (countries.length < (criteria.countryIds?.length ?? 0)) omitted.push('countries');

  return { url: `${VESTIAIRE_SEARCH_BASE_URL[language]}${query}${fragment ? `#${fragment}` : ''}`, omitted };
}
